import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Smartphone, Save, AlertTriangle, Mail, Plus, X } from 'lucide-react';

const GET_APP_CONFIG = gql`
  query GetAppConfig {
    appConfig {
      id minRequiredVersion forceUpdate iosStoreUrl androidStoreUrl paymentNotificationEmails updatedAt
    }
  }
`;

const UPDATE_APP_CONFIG = gql`
  mutation UpdateAppConfig(
    $minRequiredVersion: String
    $forceUpdate: Boolean
    $iosStoreUrl: String
    $androidStoreUrl: String
    $paymentNotificationEmails: [String!]
  ) {
    updateAppConfig(
      minRequiredVersion: $minRequiredVersion
      forceUpdate: $forceUpdate
      iosStoreUrl: $iosStoreUrl
      androidStoreUrl: $androidStoreUrl
      paymentNotificationEmails: $paymentNotificationEmails
    ) {
      id minRequiredVersion forceUpdate iosStoreUrl androidStoreUrl paymentNotificationEmails updatedAt
    }
  }
`;

const AppConfigSettings = () => {
  const { data, loading, error: queryError } = useQuery(GET_APP_CONFIG);
  const [updateAppConfig, { loading: saving }] = useMutation(UPDATE_APP_CONFIG);

  const [form, setForm] = useState({
    minRequiredVersion: '1.0.0',
    forceUpdate: false,
    iosStoreUrl: '',
    androidStoreUrl: '',
    paymentNotificationEmails: [],
  });
  const [newEmail, setNewEmail] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.appConfig) {
      const c = data.appConfig;
      setForm({
        minRequiredVersion: c.minRequiredVersion,
        forceUpdate: c.forceUpdate,
        iosStoreUrl: c.iosStoreUrl,
        androidStoreUrl: c.androidStoreUrl,
        paymentNotificationEmails: c.paymentNotificationEmails || [],
      });
    }
  }, [data]);

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (form.paymentNotificationEmails.includes(email)) return;
    setForm(f => ({ ...f, paymentNotificationEmails: [...f.paymentNotificationEmails, email] }));
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setForm(f => ({ ...f, paymentNotificationEmails: f.paymentNotificationEmails.filter(e => e !== email) }));
  };

  const handleSave = async () => {
    try {
      await updateAppConfig({ variables: form });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Save error:', err);
      alert('Erreur lors de la sauvegarde : ' + err.message);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Chargement...</div>;
  if (queryError) return <div className="p-6 text-red-500">Erreur lors du chargement de la configuration : {queryError.message}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Smartphone className="w-6 h-6 text-blue-500" />
        <h2 className="text-xl font-semibold text-gray-800">Mur de mise à jour forcée</h2>
      </div>

      {form.forceUpdate && (
        <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
          <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
          <p className="text-sm text-orange-700">
            Le mur est <strong>actif</strong> — tous les utilisateurs avec une version inférieure à <strong>v{form.minRequiredVersion}</strong> voient un écran bloquant et ne peuvent pas utiliser l'app.
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
        {/* Force update toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-800">Forcer la mise à jour</p>
            <p className="text-sm text-gray-500 mt-0.5">Bloque l'accès aux versions antérieures à la version minimale</p>
          </div>
          <button
            onClick={() => setForm(f => ({ ...f, forceUpdate: !f.forceUpdate }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.forceUpdate ? 'bg-red-500' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              form.forceUpdate ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        <div className="border-t border-gray-100" />

        {/* Min version */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Version minimale requise
          </label>
          <input
            type="text"
            value={form.minRequiredVersion}
            onChange={e => setForm(f => ({ ...f, minRequiredVersion: e.target.value }))}
            placeholder="ex: 1.2.0"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-400 mt-1">Format semver : MAJOR.MINOR.PATCH</p>
        </div>

        <div className="border-t border-gray-100" />

        {/* Store URLs */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">URLs des stores</p>
          <div>
            <label className="block text-xs text-gray-500 mb-1">App Store (iOS)</label>
            <input
              type="text"
              value={form.iosStoreUrl}
              onChange={e => setForm(f => ({ ...f, iosStoreUrl: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Play Store (Android)</label>
            <input
              type="text"
              value={form.androidStoreUrl}
              onChange={e => setForm(f => ({ ...f, androidStoreUrl: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Payment notification emails */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-emerald-500" />
          <div>
            <p className="font-medium text-gray-800">Emails de notification — Demandes de paiement</p>
            <p className="text-sm text-gray-500 mt-0.5">Ces adresses reçoivent un email à chaque nouvelle demande de paiement</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {form.paymentNotificationEmails.map(email => (
            <span key={email} className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-full">
              {email}
              <button onClick={() => removeEmail(email)} className="text-emerald-500 hover:text-emerald-700">
                <X size={13} />
              </button>
            </span>
          ))}
          {form.paymentNotificationEmails.length === 0 && (
            <p className="text-sm text-gray-400 italic">Aucun destinataire configuré</p>
          )}
        </div>

        <div className="flex gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addEmail()}
            placeholder="exemple@domaine.com"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={addEmail}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg"
          >
            <Plus size={15} />
            Ajouter
          </button>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          saved
            ? 'bg-green-500 text-white'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
      >
        <Save className="w-4 h-4" />
        {saved ? 'Sauvegardé !' : saving ? 'Sauvegarde...' : 'Sauvegarder'}
      </button>
    </div>
  );
};

export default AppConfigSettings;
